import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from "./components/ui/card";
import { Button } from "./components/ui/button";
import { Input } from "./components/ui/input";
import { Label } from "./components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "./components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "./components/ui/dialog";
import { 
  Settings, Edit, Save, X, Plus, Trash2, Clock, BookOpen, GraduationCap
} from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL ? `${process.env.REACT_APP_BACKEND_URL}/api` : '/api';

const BGKursEinstellungen = () => {
  const [einstellungen, setEinstellungen] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editItem, setEditItem] = useState(null);
  const [showDialog, setShowDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleteItem, setDeleteItem] = useState(null);
  const [isNew, setIsNew] = useState(false);
  
  const emptyForm = {
    id: '',
    name: '',
    massnahmennummer: '',
    ue_gesamt: '',
    praktikum_tage: 0,
    dozent_standard: '',
    unterricht_beginn: '09:00',
    unterricht_ende: '15:30',
    ue_pro_tag: 6.5
  };
  
  const [form, setForm] = useState(emptyForm);
  const [initializing, setInitializing] = useState(false);

  // Standard BG-Kurse für Auto-Initialisierung
  const DEFAULT_BG_KURSE = [
    {
      id: 'ambulanter_pflegeassistent',
      name: 'Ambulanter Pflegeassistent',
      massnahmennummer: 'XXX/XXXX/XXX',
      ue_gesamt: 300,
      praktikum_tage: 0,
      dozent_standard: '',
      unterricht_beginn: '09:00',
      unterricht_ende: '15:30',
      ue_pro_tag: 8.0
    },
    {
      id: 'pflegehilfskraft_betreuungsassistent',
      name: 'Pflegehilfskraft & Betreuungsassistent',
      massnahmennummer: 'XXX/XXXX/XXX',
      ue_gesamt: 429,
      praktikum_tage: 5,
      dozent_standard: '',
      unterricht_beginn: '09:00',
      unterricht_ende: '15:30',
      ue_pro_tag: 8.0
    },
    {
      id: 'pflegeassistent_lg1_lg2',
      name: 'Pflegeassistent LG1 & LG2',
      massnahmennummer: 'XXX/XXXX/XXX',
      ue_gesamt: 208,
      praktikum_tage: 0,
      dozent_standard: '',
      unterricht_beginn: '09:00',
      unterricht_ende: '15:30',
      ue_pro_tag: 8.0
    }
  ];

  // Auto-Initialisierung der BG-Kurse wenn keine vorhanden sind
  const initializeBGKurse = useCallback(async () => {
    setInitializing(true);
    try {
      for (const kurs of DEFAULT_BG_KURSE) {
        try {
          await axios.post(`${API}/bg-kurs-einstellungen`, kurs);
        } catch (err) {
          // Ignoriere Fehler wenn Kurs bereits existiert
          if (err.response?.status !== 400) {
            console.error('Fehler beim Erstellen von', kurs.id, err);
          }
        }
      }
      toast.success('BG-Kurse wurden initialisiert');
    } catch (error) {
      console.error('Initialisierungsfehler:', error);
    } finally {
      setInitializing(false);
    }
  }, []);

  const loadEinstellungen = useCallback(async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/bg-kurs-einstellungen`);
      
      // Wenn keine Einstellungen vorhanden sind, automatisch initialisieren
      if (res.data.length === 0) {
        await initializeBGKurse();
        // Neu laden nach Initialisierung
        const newRes = await axios.get(`${API}/bg-kurs-einstellungen`);
        setEinstellungen(newRes.data);
      } else {
        setEinstellungen(res.data);
      }
    } catch (error) {
      toast.error('Fehler beim Laden der Einstellungen');
    } finally {
      setLoading(false);
    }
  }, [initializeBGKurse]);

  useEffect(() => {
    loadEinstellungen();
  }, [loadEinstellungen]);

  const openEdit = (item) => {
    setForm({
      id: item.id,
      name: item.name || '',
      massnahmennummer: item.massnahmennummer || '',
      ue_gesamt: item.ue_gesamt || '',
      praktikum_tage: item.praktikum_tage || 0,
      dozent_standard: item.dozent_standard || '',
      unterricht_beginn: item.unterricht_beginn || '09:00',
      unterricht_ende: item.unterricht_ende || '15:30',
      ue_pro_tag: item.ue_pro_tag || 6.5
    });
    setEditItem(item);
    setIsNew(false);
    setShowDialog(true);
  };

  const openNew = () => {
    setForm(emptyForm);
    setEditItem(null);
    setIsNew(true);
    setShowDialog(true);
  };

  const handleSave = async () => {
    if (!form.name || !form.massnahmennummer) {
      toast.error('Name und Maßnahmennummer sind erforderlich');
      return;
    }
    
    try {
      if (isNew) {
        if (!form.id) {
          toast.error('Kurs-ID ist erforderlich');
          return;
        }
        await axios.post(`${API}/bg-kurs-einstellungen`, form);
        toast.success('Kurs-Einstellung erstellt');
      } else {
        const { id, ...updateData } = form;
        await axios.put(`${API}/bg-kurs-einstellungen/${editItem.id}`, updateData);
        toast.success('Kurs-Einstellung aktualisiert');
      }
      setShowDialog(false);
      loadEinstellungen();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Speichern');
    }
  };

  const confirmDelete = (item) => {
    setDeleteItem(item);
    setShowDeleteDialog(true);
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${API}/bg-kurs-einstellungen/${deleteItem.id}`);
      toast.success('Kurs-Einstellung gelöscht');
      setShowDeleteDialog(false);
      setDeleteItem(null);
      loadEinstellungen();
    } catch (error) {
      toast.error('Fehler beim Löschen');
    }
  };

  if (loading) {
    return (
      <div className="page-container">
        <div className="flex items-center justify-center h-64">
          <div className="loading-spinner"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-semibold flex items-center gap-2" style={{fontFamily: 'Manrope, sans-serif'}}>
            <Settings className="text-[#D9775A]" size={28} />
            BG-Kurs-Einstellungen
          </h1>
          <p className="text-sm text-gray-500">Verwaltung der Bildungsgutschein-Kurse und Maßnahmennummern</p>
        </div>
        <div className="flex gap-2">
          {einstellungen.length < 3 && (
            <Button 
              variant="outline" 
              onClick={async () => {
                await initializeBGKurse();
                loadEinstellungen();
              }}
              disabled={initializing}
            >
              {initializing ? 'Initialisiere...' : 'Kurse initialisieren'}
            </Button>
          )}
          <Button onClick={openNew} className="bg-[#D9775A] hover:bg-[#C2603F]">
            <Plus size={18} className="mr-2" /> Neuer Kurs
          </Button>
        </div>
      </div>

      {/* Info-Box wenn weniger als 3 Kurse */}
      {einstellungen.length < 3 && (
        <div className="mb-4 p-4 bg-amber-50 border border-amber-200 rounded-lg">
          <p className="text-amber-800 text-sm">
            <strong>Hinweis:</strong> Es fehlen BG-Kurse. Klicken Sie auf "Kurse initialisieren" um die 3 Standard-BG-Kurse anzulegen.
          </p>
        </div>
      )}

      {/* Kurs-Karten */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        {einstellungen.map(item => (
          <Card key={item.id} className="border-l-4 border-l-[#D9775A]">
            <CardHeader className="pb-2">
              <div className="flex justify-between items-start">
                <div>
                  <CardTitle className="text-base flex items-center gap-2">
                    <BookOpen size={18} className="text-[#D9775A]" />
                    {item.name}
                  </CardTitle>
                  <p className="text-xs text-gray-500 mt-1">ID: {item.id}</p>
                </div>
                <div className="flex gap-1">
                  <Button variant="ghost" size="sm" onClick={() => openEdit(item)}>
                    <Edit size={14} />
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => confirmDelete(item)} className="text-red-500 hover:text-red-700">
                    <Trash2 size={14} />
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <p className="text-gray-500 text-xs">Maßnahmennummer</p>
                  <p className="font-mono font-medium text-[#D9775A]">{item.massnahmennummer}</p>
                </div>
                <div>
                  <p className="text-gray-500 text-xs">Gesamt UE</p>
                  <p className="font-medium">{item.ue_gesamt || '-'}</p>
                </div>
                <div>
                  <p className="text-gray-500 text-xs">Praktikumstage</p>
                  <p className="font-medium">{item.praktikum_tage || 0}</p>
                </div>
                <div>
                  <p className="text-gray-500 text-xs">UE/Tag</p>
                  <p className="font-medium">{item.ue_pro_tag || 6.5}</p>
                </div>
                <div>
                  <p className="text-gray-500 text-xs">Unterrichtszeit</p>
                  <p className="font-medium flex items-center gap-1">
                    <Clock size={12} />
                    {item.unterricht_beginn || '09:00'} - {item.unterricht_ende || '15:30'}
                  </p>
                </div>
                <div>
                  <p className="text-gray-500 text-xs">Standard-Dozent</p>
                  <p className="font-medium">{item.dozent_standard || '-'}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {einstellungen.length === 0 && (
        <Card>
          <CardContent className="py-12 text-center text-gray-500">
            <Settings size={48} className="mx-auto mb-4 text-gray-300" />
            <p>Keine Kurs-Einstellungen vorhanden</p>
            <Button variant="outline" className="mt-4" onClick={openNew}>
              <Plus size={16} className="mr-2" /> Ersten Kurs anlegen
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Bearbeiten Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {isNew ? <Plus size={18} /> : <Edit size={18} />}
              {isNew ? 'Neuen Kurs anlegen' : 'Kurs bearbeiten'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {isNew && (
              <div>
                <Label>Kurs-ID (eindeutig, z.B. "lg1lg2")</Label>
                <Input 
                  value={form.id}
                  onChange={(e) => setForm({...form, id: e.target.value.toLowerCase().replace(/[^a-z0-9]/g, '')})}
                  placeholder="z.B. lg1lg2, pflegehilfskraft"
                />
              </div>
            )}
            <div>
              <Label>Kursname *</Label>
              <Input 
                value={form.name}
                onChange={(e) => setForm({...form, name: e.target.value})}
                placeholder="z.B. Pflegeassistent nach LG1 und LG2"
              />
            </div>
            <div>
              <Label>Maßnahmennummer *</Label>
              <Input 
                value={form.massnahmennummer}
                onChange={(e) => setForm({...form, massnahmennummer: e.target.value})}
                placeholder="z.B. 364/1075/2022"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Gesamt UE</Label>
                <Input 
                  type="number"
                  value={form.ue_gesamt}
                  onChange={(e) => setForm({...form, ue_gesamt: parseInt(e.target.value) || ''})}
                  placeholder="z.B. 186"
                />
              </div>
              <div>
                <Label>Praktikumstage</Label>
                <Input 
                  type="number"
                  value={form.praktikum_tage}
                  onChange={(e) => setForm({...form, praktikum_tage: parseInt(e.target.value) || 0})}
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Unterricht Beginn</Label>
                <Input 
                  type="time"
                  value={form.unterricht_beginn}
                  onChange={(e) => setForm({...form, unterricht_beginn: e.target.value})}
                />
              </div>
              <div>
                <Label>Unterricht Ende</Label>
                <Input 
                  type="time"
                  value={form.unterricht_ende}
                  onChange={(e) => setForm({...form, unterricht_ende: e.target.value})}
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>UE pro Tag</Label>
                <Input 
                  type="number"
                  step="0.5"
                  value={form.ue_pro_tag}
                  onChange={(e) => setForm({...form, ue_pro_tag: parseFloat(e.target.value) || 6.5})}
                />
              </div>
              <div>
                <Label>Standard-Dozent</Label>
                <Input 
                  value={form.dozent_standard}
                  onChange={(e) => setForm({...form, dozent_standard: e.target.value})}
                  placeholder="z.B. Julia Müller"
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)}>
              Abbrechen
            </Button>
            <Button onClick={handleSave} className="bg-[#D9775A] hover:bg-[#C2603F]">
              <Save size={16} className="mr-2" /> Speichern
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Löschen Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-red-600">Kurs-Einstellung löschen?</DialogTitle>
            <DialogDescription>
              Möchten Sie die Kurs-Einstellung "{deleteItem?.name}" wirklich löschen? Diese Aktion kann nicht rückgängig gemacht werden.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteDialog(false)}>
              Abbrechen
            </Button>
            <Button variant="destructive" onClick={handleDelete}>
              <Trash2 size={16} className="mr-2" /> Löschen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BGKursEinstellungen;
